import { MAX_DOSSIER_FILE_BYTES } from "@/features/dossiers/types";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import { cn } from "@/lib/utils";
import { CircleNotchIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useState, type DragEvent } from "react";

/**
 * Drag-and-drop for a whole area: `children` is what is shown, and the area
 * lights up while a file is held over it. Leaving a child element fires
 * `dragleave` on the parent too, hence the depth counter rather than a flag.
 */
export function useFileDrop(onFiles: (files: File[]) => void, enabled = true) {
  const [depth, setDepth] = useState(0);

  const hasFiles = (event: DragEvent) =>
    enabled && Array.from(event.dataTransfer.types).includes("Files");

  return {
    dragging: depth > 0,
    handlers: {
      onDragEnter: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDepth((value) => value + 1);
      },
      onDragOver: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      },
      onDragLeave: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        setDepth((value) => Math.max(0, value - 1));
      },
      onDrop: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDepth(0);
        const files = Array.from(event.dataTransfer.files);
        if (files.length) onFiles(files);
      },
    },
  };
}

/** The dashed target: click to browse, or drop on it (or on the area around it). */
export function DropZone({
  onBrowse,
  dragging,
  uploading,
  compact = false,
}: {
  onBrowse: () => void;
  dragging: boolean;
  uploading: number;
  compact?: boolean;
}) {
  const { t } = useTranslation("dossiers");

  return (
    <button
      type="button"
      onClick={onBrowse}
      disabled={uploading > 0}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-md border border-dashed text-sm text-muted-foreground transition-colors",
        compact ? "px-3 py-2" : "flex-col px-4 py-6",
        dragging
          ? "border-primary bg-primary/5 text-primary"
          : "border-input hover:border-slate-400 hover:text-foreground",
        uploading > 0 && "cursor-wait",
      )}
    >
      {uploading > 0 ? (
        <CircleNotchIcon className="size-5 animate-spin" aria-hidden />
      ) : (
        <UploadSimpleIcon className="size-5" weight="fill" aria-hidden />
      )}
      <span>
        {uploading > 0
          ? t("drop.uploading", { count: uploading })
          : dragging
            ? t("drop.release")
            : compact
              ? t("drop.compact")
              : t("drop.prompt")}
      </span>
      {!compact && !uploading && (
        <span className="text-xs">
          {t("drop.maxSize", { size: formatFileSize(MAX_DOSSIER_FILE_BYTES) })}
        </span>
      )}
    </button>
  );
}
